//Main File of frontend 

import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useNavigate,
  useLocation,
} from "react-router-dom";

import Header from "./components/Header";
import Footer from "./components/Footer";
import Sidebar from "./components/Sidebar";
import ToolDetailsWrapper from "./components/ToolDetailsWrapper";
import AddTool from "./components/AddTool";
import ConfirmTool from "./components/ConfirmTool";
import AboutUs from "./components/AboutUs"
import PrivacyPolicy from "./components/PrivacyPolicy";
import Pagination from "./components/Pagination";
import SortDrawer from "./components/SortDrawer";
import Spinner from "./components/Spinner";
import ToolCard from "./components/ToolCard";
import useFetchTools from "./hooks/useFetchTools";
import PlanningTypeInfo from "./components/PlanningTypeInfo";
import HomePage from "./components/HomePage";
import DiscoverPage from "./components/DiscoverPage";
import KnowledgeGraphPage from "./components/KnowledgeGraphPage";

function App() {
  const [tools, setTools] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [toolsPerPage] = useState(12);
  const [sortOptions, setSortOptions] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [showSortOptions, setShowSortOptions] = useState(false);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [selectedCompletenessScore, setSelectedCompletenessScore] = useState([]);
  const [selectedContributors, setSelectedContributors] = useState(false);
  const [selectedOperatingSystems, setSelectedOperatingSystems] = useState([]);
  const [selectedPlanningClasses, setSelectedPlanningClasses] = useState([]);
  const [selectedPlanningTypes, setSelectedPlanningTypes] = useState([]);
  const [selectedDocumentation, setSelectedDocumentation] = useState(false);
  const [selectedExplanationAbstract, setSelectedExplanationAbstract] = useState(false);
  const [selectedPlannerReferences, setSelectedPlannerReferences] = useState(false);
  const [selectedExecutable, setSelectedExecutable] = useState(false);
  const [selectedSourceCode, setSelectedSourceCode] = useState(false);
  const [selectedEnvironmentNotes, setSelectedEnvironmentNotes] = useState(false);
  const [selectedImplementationLanguage, setSelectedImplementationLanguage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [drawerHeight, setDrawerHeight] = useState(0);
  const [availablePlanningTypes, setAvailablePlanningTypes] = useState([]);  // New state
  const [yearRange, setYearRange] = useState({ min: 1997, max: new Date().getFullYear() });  // Updated default min year to 1900

  const cardContainerStyle = {
    marginTop: `${drawerHeight}px`,
    transition: "margin-top 0.3s ease-in-out",
    minHeight: loading || tools.length === 0 ? "80vh" : "auto",
  };

  useFetchTools({
    search,
    currentPage,
    sortOptions,
    sortOrder,
    selectedLanguages,
    selectedCompletenessScore,
    selectedContributors,
    selectedOperatingSystems,
    selectedPlanningClasses,
    selectedPlanningTypes,
    setAvailablePlanningTypes,
    selectedDocumentation,
    selectedExplanationAbstract,
    selectedPlannerReferences,
    selectedExecutable,
    selectedSourceCode,
    selectedEnvironmentNotes,
    selectedImplementationLanguage,
    yearRange,  // Add year range to the fetch tools hook
    setTools,
    setLoading,
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [search, selectedLanguages, selectedCompletenessScore, selectedContributors, selectedOperatingSystems, selectedPlanningClasses, selectedPlanningTypes, yearRange]);  // Add yearRange to dependencies

  const navigate = useNavigate();
  const location = useLocation();
  const isToolDetailsPage = location.pathname.startsWith("/tools/");
  const isAddToolPage = location.pathname.startsWith("/add-tool");
  const isConfirmToolPage = location.pathname.startsWith("/confirm-tool");
  // const isAboutUSPage = '';
  const isAboutUSPage = location.pathname.startsWith("/about-us");
  const isPrivacyPolicyPage = location.pathname.startsWith("/privacy-policy");
  const isInfoPage = location.pathname.startsWith("/planning-type-info");
  const isHomePage = location.pathname === "/";
  const isKnowledgeGraphPage = location.pathname.startsWith("/knowledge-graph");

  

  // For randomization when no filters are applied at the time of initial render
  // Check if any filters or custom sort are active
  const hasActiveFilters = 
    search.trim() !== "" ||
    selectedLanguages.length > 0 ||
    selectedCompletenessScore.length > 0 ||
    selectedContributors ||
    selectedOperatingSystems.length > 0 ||
    selectedPlanningClasses.length > 0 ||
    selectedPlanningTypes.length > 0 ||
    selectedDocumentation ||
    selectedExplanationAbstract ||
    selectedPlannerReferences ||
    selectedExecutable ||
    selectedSourceCode ||
    selectedEnvironmentNotes ||
    selectedImplementationLanguage ||
    sortOptions !== null ||
    yearRange.min !== 1997 ||
    yearRange.max !== new Date().getFullYear();

  // Sort planners: random if no filters, by year if filters are active
  const sortedTools = [...tools].sort((a, b) => {
    if (hasActiveFilters) {
      // Sort by year (descending) with completeness score as tie-breaker
      const yearA = parseInt(a.year) || 0;
      const yearB = parseInt(b.year) || 0;
      if (yearA !== yearB) return yearB - yearA;
      const scoreA = parseFloat(a.completenessScore) || 0;
      const scoreB = parseFloat(b.completenessScore) || 0;
      return scoreB - scoreA;
    } else {
      // Show randomly when no filters are applied
      return Math.random() - 0.5;
    }
  });

  const indexOfLastTool = currentPage * toolsPerPage;
  const indexOfFirstTool = indexOfLastTool - toolsPerPage;
  const currentTools = sortedTools.slice(indexOfFirstTool, indexOfLastTool);
  const totalPages = Math.ceil(tools.length / toolsPerPage);

  return (
    <div className="flex flex-col min-h-screen">
      <Header />

      <div className="flex flex-1 justify-center">
        <div className="w-full" style={{ maxWidth: isKnowledgeGraphPage ? "100%" : "1400px", paddingLeft: isKnowledgeGraphPage ? "0" : "1.5rem", paddingRight: isKnowledgeGraphPage ? "0" : "1.5rem" }}>
          <div className="flex items-start gap-1">
            {!isInfoPage && !isToolDetailsPage && !isAddToolPage && !isConfirmToolPage && !isAboutUSPage && !isPrivacyPolicyPage && !isHomePage && !isKnowledgeGraphPage && (
              <div className="w-72 flex-shrink-0">
                <Sidebar
                  selectedLanguages={selectedLanguages}
                  setSelectedLanguages={setSelectedLanguages}
                  selectedCompletenessScore={selectedCompletenessScore}
                  setSelectedCompletenessScore={setSelectedCompletenessScore}
                  selectedContributors={selectedContributors}
                  setSelectedContributors={setSelectedContributors}
                  selectedOperatingSystems={selectedOperatingSystems}
                  setSelectedOperatingSystems={setSelectedOperatingSystems}
                  selectedPlanningClasses={selectedPlanningClasses}
                  setSelectedPlanningClasses={setSelectedPlanningClasses}
                  selectedPlanningTypes={selectedPlanningTypes}
                  setSelectedPlanningTypes={setSelectedPlanningTypes}
                  availablePlanningTypes={availablePlanningTypes}
                  selectedDocumentation={selectedDocumentation}
                  setSelectedDocumentation={setSelectedDocumentation}
                  selectedExplanationAbstract={selectedExplanationAbstract}
                  setSelectedExplanationAbstract={setSelectedExplanationAbstract}
                  selectedPlannerReferences={selectedPlannerReferences}
                  setSelectedPlannerReferences={setSelectedPlannerReferences}
                  selectedExecutable={selectedExecutable}
                  setSelectedExecutable={setSelectedExecutable}
                  selectedSourceCode={selectedSourceCode}
                  setSelectedSourceCode={setSelectedSourceCode}
                  selectedEnvironmentNotes={selectedEnvironmentNotes}
                  setSelectedEnvironmentNotes={setSelectedEnvironmentNotes}
                  selectedImplementationLanguage={selectedImplementationLanguage}
                  setSelectedImplementationLanguage={setSelectedImplementationLanguage}
                  yearRange={yearRange}
                  setYearRange={setYearRange}
                />
              </div>
            )}
            <main className="flex-1" style={{ padding: isKnowledgeGraphPage ? "0" : "1rem" }}>
              <Routes>
            <Route
              path="/tools/:id"
              element={<ToolDetailsWrapper tools={tools} />}
            />
            <Route path="/add-tool" element={<AddTool />} />
            <Route path="/confirm-tool" element={<ConfirmTool />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/planning-type-info" element={<PlanningTypeInfo />} />
            <Route path="/" element={<HomePage />} />
            <Route
              path="/discover"
              element={
                <DiscoverPage
                  search={search}
                  setSearch={setSearch}
                  loading={loading}
                  tools={tools}
                  currentTools={currentTools}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  setCurrentPage={setCurrentPage}
                  showSortOptions={showSortOptions}
                  setShowSortOptions={setShowSortOptions}
                  setDrawerHeight={setDrawerHeight}
                  drawerHeight={drawerHeight}
                  sortOptions={sortOptions}
                  setSortOptions={setSortOptions}
                  sortOrder={sortOrder}
                  setSortOrder={setSortOrder}
                  navigate={navigate}
                />
              }
            />
            <Route path="/knowledge-graph" element={<KnowledgeGraphPage />} />
          </Routes>
        </main>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default function AppWithRouter() {
  return (
    <Router>
      <App />
    </Router>
  );
}
